import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import './App.css';

import { Navigation } from './components/navigation/navigation';
import AuthenticationPage from './pages/authentication-page/authentication-page';
import MapPage from './pages/map-page/map-page';
import { LocationHistoryPage } from './pages/location-history-page/location-history-page';
import { ShoppingListPage } from './pages/shopping-list/shopping-list';
import { ManageYourTeam } from './pages/manage-your-team-page/manage-your-team-page';
import { NotificationPage } from './pages/notification-page/notification-page';
import { PartLookUp } from './pages/part-lookup/part-lookup';

import { SELECTED_ASSET } from './constants/bucket.contants';
import { IDLE_INTERVAL, KEYDOWN, TIMEOUT } from './constants/idle.constants';
import { AppConfigContext } from './contexts/app-config.context';
import { useAppConfigStore } from './hooks/app-config-store.hook';
import initializeFirebase from './utils/firebase.util';
import { logout } from './utils/logout.util';
import { routePath } from './constants/navigation.constants';

import { Footer } from "@weirgroup/weir-dls"
import { footerLinks } from './constants/footer.constants';
import ErrorPage from './pages/error-page/error-page';
import { useTranslation } from 'react-i18next';
import { useDarkMode } from './hooks/use-darkmode';


interface RoutesType {
    id: number
    path: string
    element: JSX.Element,
    darkMode?: boolean
}

initializeFirebase()
const queryClient = new QueryClient()

function App() {
    const { t } = useTranslation();
    const [showMapPage, setShowMapPage] = useState<boolean>(false)
    const [selectedAsset, setSelectedAsset] = useState<any>()

    const appConfigStore = useAppConfigStore()

    const timeout = TIMEOUT
    const [elapsed, setElapsed] = useState(0)
    const [isIdle, setIsIdle] = useState(false)
    
    const { reset, getElapsedTime } = useIdleTimer({
        timeout,
        onActive: () => setIsIdle(false),
        onIdle: () => setIsIdle(true),
    })
    const {darkMode} = useDarkMode()
    useEffect(() => {
        sessionStorage.setItem(SELECTED_ASSET, JSON.stringify(selectedAsset))
    }, [selectedAsset])

    useEffect(() => {
        setSelectedAsset(null)
        const keyDownHandler = () => {
            setIsIdle(false)
            reset()
        }
        document.addEventListener(KEYDOWN, keyDownHandler)

        setElapsed(getElapsedTime())
        setInterval(() => {
            setElapsed(getElapsedTime())
        }, 1000)
    }, [])

    useEffect(() => {
        reset()
    }, [isIdle])

    useEffect(() => {
        if (elapsed >= IDLE_INTERVAL && isIdle) {
            reset()
            logout()
        }
    }, [elapsed])
    const routes: RoutesType[] = [
        {
            id: 1,
            path: '/',
            element: (
                <MapPage
                    selectedAsset={selectedAsset}
                    setSelectedAsset={setSelectedAsset}
                />
            ),
        },
        {
            id: 2,
            path: routePath.HOMEPAGE,
            element: (
                <MapPage
                    selectedAsset={selectedAsset}
                    setSelectedAsset={setSelectedAsset}
                />

            ),
        },
        {
            id: 3,
            path: routePath.LOCATION_HISTORY,
            element: <LocationHistoryPage selectedAsset={selectedAsset} />,
        },
        { id: 4, path: routePath.SHOPPING_LIST, element: <ShoppingListPage /> },
        { id: 5, path: routePath.MANAGE_YOUR_TEAM, element: <ManageYourTeam /> },
        { id: 6, path: routePath.NOTIFICATION, element: <NotificationPage /> },
        { id: 7, path: routePath.SERIAL_LOOKUP, element: <PartLookUp /> },
        { id: 8, path: '*', element: <ErrorPage /> }
    ]

    return (
        <>
            {showMapPage ? (
                <QueryClientProvider client={queryClient}>
                    <AppConfigContext.Provider value={appConfigStore}>
                        <Router>
                            <Navigation isAuthenticated />
                            <Routes>
                                {routes.map((route: RoutesType) => (
                                    <Route
                                        path={route.path}
                                        element={route.element}
                                        key={route.id}
                                    />
                                ))}
                            </Routes>
                            <Footer
                                darkMode={darkMode}
                                logoName={"WeirLogo"}
                                NavigationLinks={footerLinks}
                                InformationText={t("siteInformation")}
                                CopyrightText={t("copyrightText")}
                            />
                        </Router>
                    </AppConfigContext.Provider>
                </QueryClientProvider>
            ) : (
                <AuthenticationPage setShowMapPage={setShowMapPage} />
            )}
        </>
    )
}

export default App
